import { useState, useEffect, useCallback } from 'react';
import { UserRoleManager } from '../utils/userRole';
import { UserRole, UserRoleInfo, UserInfo } from '../types';

/**
 * 用户角色管理Hook
 */
export const useUserRole = () => {
  const [roleInfo, setRoleInfo] = useState<UserRoleInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // 获取当前用户角色信息
  const getCurrentRole = useCallback(() => {
    const currentRole = UserRoleManager.getCurrentUserRole();
    setRoleInfo(currentRole);
    setIsLoading(false);
    return currentRole;
  }, []);

  // 检查是否为管理员
  const isAdmin = useCallback(() => {
    return roleInfo?.isAdmin || false;
  }, [roleInfo]);

  // 检查是否为审核员
  const isAssessor = useCallback(() => {
    return roleInfo?.isAssessor || false;
  }, [roleInfo]);

  // 检查是否为操作员
  const isOperator = useCallback(() => {
    return roleInfo?.isOperator || false;
  }, [roleInfo]);

  // 检查是否为普通用户
  const isUser = useCallback(() => {
    return roleInfo?.isUser || false;
  }, [roleInfo]);

  // 检查是否具有指定权限
  const hasPermission = useCallback((permission: string) => {
    return roleInfo?.permissions.includes(permission) || false;
  }, [roleInfo]);

  // 检查是否具有多个权限中的任意一个
  const hasAnyPermission = useCallback((permissions: string[]) => {
    return permissions.some(permission => hasPermission(permission));
  }, [hasPermission]);

  // 检查是否具有所有指定权限
  const hasAllPermissions = useCallback((permissions: string[]) => {
    return permissions.every(permission => hasPermission(permission));
  }, [hasPermission]);

  // 更新用户角色信息
  const updateUserRole = useCallback((userInfo: UserInfo) => {
    UserRoleManager.updateUserRoleInfo(userInfo);
    getCurrentRole();
  }, [getCurrentRole]);

  // 清除用户角色信息
  const clearUserRole = useCallback(() => {
    UserRoleManager.clearUserRoleInfo();
    setRoleInfo(null);
    setIsLoading(false);
  }, []);

  // 组件挂载时获取用户角色信息
  useEffect(() => {
    getCurrentRole();
  }, [getCurrentRole]);

  return {
    roleInfo,
    isLoading,
    isAdmin: isAdmin(),
    isAssessor: isAssessor(),
    isOperator: isOperator(),
    isUser: isUser(),
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    updateUserRole,
    clearUserRole,
    getCurrentRole
  };
};
